/**
 * 천장 패널 배치 — 흐름 방향 하나로 결합 자동
 * - 첫 패널(왼/위 끝, 벽 위에 걸침) = 두께별 가장자리 사이즈 (100T 995 등, 끌 수 있음)
 * - 나머지: 구간 채우기 규칙(SegRule)으로 사이즈 결정 (없으면 기본 분할: 900 채우고 나머지 끝)
 * - 접합: 패널 사이마다 간격 (전체 = Σ패널 + (장수−1)×간격) — 이전 시스템 실제 기록과 같음
 *   예) 5200 = 995 + 900×4 + 585 + 4×5
 * - 결합: 벽처럼 흐름 방향 끝이 양수 (앞 패널 양수가 다음 패널 음수에 꽂힘), 벽에 닿는 양 끝은 없음
 * - flow 'lo' = 왼/위 쪽으로 (기본, 결합 삼각형이 위/왼쪽을 향함), 'hi' = 오른/아래 쪽으로
 */
import { CeilingPanel } from '../types';
import { SegRule, FillSpec, fillSegment, defaultRule } from './segmentFill';

export type CeilingFlow = 'lo' | 'hi';

/** 두께별 천장 첫 패널(벽 위에 걸치는 패널) 사이즈 — 이전 시스템 DB 코드표 '판넬천장사이즈' */
const CEILING_EDGE: Record<number, number> = { 75: 973, 100: 995, 125: 1023, 150: 1048 };
export const ceilingEdgeSize = (thick: number) => CEILING_EDGE[thick] ?? 900 + thick - 5;

export function layoutCeiling(
  H: number, V: number, dir: 'H' | 'V', flow: CeilingFlow, rule: SegRule | null, spec: FillSpec, edge: number | null = null,
): { panels: CeilingPanel[]; total: number; error: string | null } {
  const length = dir === 'H' ? H : V;   // 패널 길이
  const total = dir === 'H' ? V : H;    // 나누는 방향 길이
  // 규칙이 채우는 구간: fillSegment는 장수×간격이라 간격 하나를 더해 (장수−1)×간격으로 맞춤
  const rest = (edge ? total - edge - spec.space : total) + spec.space;
  let { sizes, error } = fillSegment(rest, rule ?? defaultRule(spec), spec);
  if (error) sizes = fillSegment(rest, defaultRule(spec), spec).sizes;
  const all = edge ? [edge, ...sizes] : sizes;
  // 흐름 쪽 끝 = 양수: lo로 흐르면 왼/위 끝 양수·오른/아래 끝 음수
  const [loJ, hiJ] = flow === 'lo' ? (['yangsu', 'eumsu'] as const) : (['eumsu', 'yangsu'] as const);
  let pos = 0;
  const panels = all.map((size, i) => {
    const p: CeilingPanel = {
      index: i, pos, size, length, isCustom: size !== spec.standard && !(edge && i === 0),
      leftType: i === 0 ? 'none' : loJ,
      rightType: i === all.length - 1 ? 'none' : hiJ,
    };
    pos += size + spec.space;
    return p;
  });
  return { panels, total, error };
}
